import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import DashboardView from "../../components/dashboard/DashboardView";
import AddCommitmentDialog from "../../components/app/AddCommitmentDialog";
import { Skeleton } from "../../components/ui/skeleton";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Plus, Sparkles, ShieldCheck, PlusCircle } from "lucide-react";
import { formatINR } from "../../lib/format";
import { apiErr } from "../../lib/api";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { openAdd, refreshKey, triggerRefresh } = useOutletContext();
  const [data, setData] = useState(null);
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([api.get("/dashboard"), api.get("/commitments")]);
      setData(d.data); setCommitments(c.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const onSelect = (c) => { setEditing(c); setEditOpen(true); };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-44 w-full rounded-2xl" />
        <div className="grid md:grid-cols-2 gap-4"><Skeleton className="h-56 rounded-2xl" /><Skeleton className="h-56 rounded-2xl" /></div>
        <Skeleton className="h-40 w-full rounded-2xl" />
      </div>
    );
  }

  const empty = commitments.length === 0;
  const reserveUnset = data?.position && !data.position.protected_reserve;
  const onlyOne = commitments.length === 1;

  const suggestedReserve = (() => {
    if (!data?.position) return 0;
    const p = data.position;
    const base = p.monthly_recurring > 0 ? p.monthly_recurring : (p.monthly_income || 0) * 0.2;
    return Math.max(1000, Math.ceil(base / 500) * 500);
  })();

  const acceptReserve = async () => {
    try {
      const { data: ip } = await api.get("/income-profile");
      await api.put("/income-profile", {
        ...(ip || {}),
        monthly_income: ip?.monthly_income ?? data.position.monthly_income,
        protected_reserve: suggestedReserve,
      });
      toast.success(`Protected reserve set to ${formatINR(suggestedReserve)}.`);
      triggerRefresh(); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="mb-5">
        <p className="text-sm text-muted-foreground">Hello, {user?.name?.split(" ")[0] || "there"}</p>
        <h1 className="font-heading text-2xl font-medium text-ink">Your commitment position</h1>
      </div>

      {empty ? (
        <Card className="rounded-2xl border-border shadow-card-sm p-10 text-center" data-testid="dashboard-empty">
          <div className="h-14 w-14 rounded-2xl bg-mint-light text-mint flex items-center justify-center mx-auto"><Sparkles size={24} /></div>
          <h3 className="font-heading text-xl font-medium text-ink mt-4">Let's add your first commitment</h3>
          <p className="text-slatey mt-2 max-w-sm mx-auto">Add a subscription, EMI, bill or autopay — manually, by pasting a message, or from a screenshot.</p>
          <Button onClick={openAdd} className="rounded-full mt-6 bg-navy text-white hover:bg-navy-light" data-testid="dashboard-empty-add">
            <Plus size={18} className="mr-1.5" />Add Commitment
          </Button>
        </Card>
      ) : (
        <>
          <div className="space-y-3 mb-5">
            {reserveUnset && (
              <Card className="rounded-2xl border-border shadow-card-sm p-4 bg-mint-light/40" data-testid="tip-set-reserve">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-white text-mint flex items-center justify-center shrink-0"><ShieldCheck size={18} strokeWidth={1.5} /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink">Set a protected reserve</p>
                    <p className="text-xs text-slatey">We suggest <span className="font-medium text-ink tnum">{formatINR(suggestedReserve)}</span> — about a month of your commitments to keep untouched.</p>
                  </div>
                  <Button size="sm" onClick={acceptReserve} className="rounded-full bg-navy text-white hover:bg-navy-light shrink-0 h-9" data-testid="tip-set-reserve-btn">Set {formatINR(suggestedReserve)}</Button>
                </div>
                <button onClick={() => nav("/app/profile")} className="text-xs text-mint hover:underline mt-2 ml-[52px]" data-testid="tip-set-reserve-custom">Or set a custom amount</button>
              </Card>
            )}
            {onlyOne && (
              <Card className="rounded-2xl border-border shadow-card-sm p-4 flex items-center gap-3" data-testid="tip-add-more">
                <div className="h-10 w-10 rounded-xl bg-secondary text-navy flex items-center justify-center shrink-0"><PlusCircle size={18} strokeWidth={1.5} /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink">Add one more commitment</p>
                  <p className="text-xs text-slatey">You've added one so far. Add a few more to see your full commitment picture come together.</p>
                </div>
                <Button size="sm" onClick={openAdd} className="rounded-full bg-mint text-white hover:bg-mint/90 shrink-0 h-9" data-testid="tip-add-more-btn"><Plus size={14} className="mr-1" />Add</Button>
              </Card>
            )}
          </div>
          <DashboardView data={data} commitments={commitments} onSelect={onSelect} />
        </>
      )}

      <AddCommitmentDialog open={editOpen} onOpenChange={setEditOpen} existing={editing} onSaved={() => { triggerRefresh(); load(); }} />
    </div>
  );
}
